import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import ConvergenceMotif from "@/components/ConvergenceMotif";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav(loc.state?.from || "/app", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-sand grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-court text-sand p-12 flex-col">
        <Link to="/"><Logo tone="sand" /></Link>
        <div className="mt-auto">
          <div className="eyebrow-light mb-6">Resolve with respect</div>
          <h2 className="font-display text-[44px]" style={{ fontWeight: 400, lineHeight: 1.04 }}>
            A fair hearing,<br />fully online.
          </h2>
          <div className="mt-10 max-w-md">
            <p className="text-sand/75 text-[14px] pretty leading-relaxed">
              Mediation, arbitration and conciliation with certified Indian mediators & arbitrators — filed, heard and closed from a single dashboard.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center text-[13px] text-ink/65 hover:text-ink mb-8"><ArrowLeft className="h-4 w-4 mr-1" />Back</Link>
          <div className="eyebrow">Sign in</div>
          <h1 className="font-display text-[40px] text-court mt-3" style={{ fontWeight: 400, lineHeight: 1.05 }}>Welcome back.</h1>
          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
            <Field id="email" testid="login-email" label="Email" type="email" value={email} onChange={setEmail} />
            <Field id="password" testid="login-password" label="Password" type="password" value={password} onChange={setPassword} />
            {err && <div data-testid="login-error" className="text-[13px] text-clay border-l-2 border-clay pl-3">{err}</div>}
            <button type="submit" disabled={loading} data-testid="login-submit" className="inline-flex items-center gap-2 px-5 py-3 bg-clay text-sand hover:bg-[hsl(17_64%_42%)] text-sm w-full justify-center" style={{ borderRadius: 3 }}>
              {loading ? "Signing in…" : "Sign in"} <ArrowUpRight className="h-4 w-4" />
            </button>
          </form>
          <div className="mt-8 text-[13px] text-ink/65">
            New here? <Link to="/register" className="text-clay underline-offset-4 hover:underline" data-testid="login-to-register">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ id, testid, label, type = "text", value, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="eyebrow block mb-2">{label}</label>
      <Input id={id} data-testid={testid} type={type} required value={value} onChange={(e) => onChange(e.target.value)}
        className="h-12 rounded-none bg-transparent border-0 border-b border-ink/25 focus:border-clay text-ink placeholder:text-ink/40 px-0" />
    </div>
  );
}
